import React from 'react';
import { Box, Typography, Divider } from '@mui/material';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { CalendarEventDto } from '@/types/calendar';

interface FoodComponent {
  name: string;
  unit?: string;
  portionName?: string;
  portionCount?: number;
  totalAmount?: number;
}

interface FoodDetails {
  rawInput?: string;
  totals?: { kcal?: number | null; protein?: number | null; fat?: number | null; carbs?: number | null } | null;
  components?: FoodComponent[];
}

export const FoodEventDetails: React.FC<{ event: CalendarEventDto }> = ({ event }) => {
  const details = (event.details ?? {}) as FoodDetails;
  const { rawInput, totals, components } = details;
  const isPending = event.nutritionStatus === 'pending';

  if (!rawInput && !totals && !isPending && !components?.length) return null;

  return (
    <>
      {rawInput && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', lineHeight: 1.4, mt: 0.5 }}>
          «{rawInput}»
        </Typography>
      )}

      {(totals || isPending) && (
        <>
          <Divider sx={{ my: 1 }} />
          {isPending || !totals ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
              <HourglassEmptyIcon sx={{ fontSize: 14 }} />
              <Typography variant="caption">Нутрієнти розраховуються...</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {totals.kcal != null && (
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{totals.kcal}</Typography>
                  <Typography variant="caption" color="text.secondary">ккал</Typography>
                </Box>
              )}
              {totals.protein != null && (
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#e53935' }}>{totals.protein}г</Typography>
                  <Typography variant="caption" color="text.secondary">білки</Typography>
                </Box>
              )}
              {totals.fat != null && (
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#fb8c00' }}>{totals.fat}г</Typography>
                  <Typography variant="caption" color="text.secondary">жири</Typography>
                </Box>
              )}
              {totals.carbs != null && (
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#43a047' }}>{totals.carbs}г</Typography>
                  <Typography variant="caption" color="text.secondary">вуглеводи</Typography>
                </Box>
              )}
            </Box>
          )}
        </>
      )}

      {!!components?.length && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Склад
          </Typography>
          <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {components.map((c, i) => (
              <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption">{c.name}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1, flexShrink: 0 }}>
                  {c.portionCount && c.portionName
                    ? `${c.portionCount} ${c.portionName}`
                    : c.totalAmount != null
                    ? `${c.totalAmount}${c.unit ? ` ${c.unit}` : ''}`
                    : ''}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
    </>
  );
};
